import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createNotification,
  deleteNotification,
  listNotifications,
  notificationBelongsToEntry,
  parseDictionaryNotifications,
  updateNotification,
  type DictionaryNotification,
  type PatchNotificationBody,
} from '../api/notificationApi';

type Variant = 'subanta' | 'tinganta';

export type EntryNotificationsPanelProps = {
  variant: Variant;
  parentId: number;
  /** From GET subanta/:id or GET tinganta/:id relations; may be empty if API omits them. */
  rawNotifications: unknown[] | undefined;
  /** Refetch parent entity (e.g. getSubanta) so nested relations stay in sync. */
  onReload: () => Promise<void>;
};

type NotificationFormValues = {
  email: string;
  phone: string;
  comment: string;
  resolved: boolean;
};

const CREATE_FORM_INITIAL: NotificationFormValues = {
  email: 'internal@gmail.com',
  phone: '09898',
  comment: '',
  resolved: false,
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || 'Save failed';
}

export function EntryNotificationsPanel({
  variant,
  parentId,
  rawNotifications,
  onReload,
}: EntryNotificationsPanelProps) {
  const [rows, setRows] = useState<DictionaryNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<DictionaryNotification | null>(null);
  const [toggleId, setToggleId] = useState<number | null>(null);
  const [createForm] = Form.useForm<NotificationFormValues>();
  const [editForm] = Form.useForm<NotificationFormValues>();

  const embedded = useMemo(() => {
    const parsed = parseDictionaryNotifications(rawNotifications);
    return parsed.filter((n) => notificationBelongsToEntry(n, variant, parentId));
  }, [rawNotifications, variant, parentId]);

  const syncRows = useCallback(async () => {
    if (embedded.length > 0) {
      setRows(embedded);
      return;
    }
    setLoading(true);
    try {
      const all = await listNotifications();
      setRows(all.filter((n) => notificationBelongsToEntry(n, variant, parentId)));
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to load notifications');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [embedded, variant, parentId]);

  useEffect(() => {
    void syncRows();
  }, [syncRows]);

  const afterMutation = useCallback(async () => {
    await onReload();
    await syncRows();
  }, [onReload, syncRows]);

  const openCreate = () => {
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      const v = await createForm.validateFields();
      setBusy(true);
      const body =
        variant === 'subanta'
          ? {
              subantaId: parentId,
              email: v.email.trim(),
              phone: v.phone.trim(),
              comment: v.comment.trim(),
              resolved: v.resolved,
            }
          : {
              tingantaId: parentId,
              email: v.email.trim(),
              phone: v.phone.trim(),
              comment: v.comment.trim(),
              resolved: v.resolved,
            };
      await createNotification(body);
      message.success('Notification created');
      setCreateOpen(false);
      createForm.setFieldsValue(CREATE_FORM_INITIAL);
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (n: DictionaryNotification) => {
    setEditing(n);
    editForm.setFieldsValue({
      email: n.email,
      phone: n.phone,
      comment: n.comment,
      resolved: n.resolved,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      const v = await editForm.validateFields();
      const next = {
        email: v.email.trim(),
        phone: v.phone.trim(),
        comment: v.comment.trim(),
        resolved: v.resolved,
      };
      const patch: PatchNotificationBody = {};
      if (editing.email !== next.email) patch.email = next.email;
      if (editing.phone !== next.phone) patch.phone = next.phone;
      if (editing.comment !== next.comment) patch.comment = next.comment;
      if (editing.resolved !== next.resolved) patch.resolved = next.resolved;
      if (Object.keys(patch).length === 0) {
        message.info('No changes');
        setEditOpen(false);
        setEditing(null);
        return;
      }
      setBusy(true);
      await updateNotification(editing.id, patch);
      message.success('Notification updated');
      setEditOpen(false);
      setEditing(null);
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onToggleResolved = async (n: DictionaryNotification, resolved: boolean) => {
    if (n.resolved === resolved) return;
    setToggleId(n.id);
    try {
      await updateNotification(n.id, { resolved });
      message.success('Updated');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setToggleId(null);
    }
  };

  const onDelete = async (id: number) => {
    setBusy(true);
    try {
      await deleteNotification(id);
      message.success('Notification deleted');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const required = (label: string) => [{ required: true, message: `${label} is required` }];

  const columns: ColumnsType<DictionaryNotification> = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    { title: 'Email', dataIndex: 'email', ellipsis: true, width: 160 },
    { title: 'Phone', dataIndex: 'phone', width: 130, ellipsis: true },
    { title: 'Comment', dataIndex: 'comment', ellipsis: true },
    {
      title: 'Resolved',
      dataIndex: 'resolved',
      width: 96,
      render: (_, n) => (
        <Switch
          checked={n.resolved}
          loading={toggleId === n.id}
          onChange={(checked) => void onToggleResolved(n, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 160,
      fixed: 'right',
      render: (_, n) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(n)}>
            Edit
          </Button>
          <Popconfirm title="Delete this notification?" onConfirm={() => void onDelete(n.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const formItems = (
    <>
      <Form.Item name="email" label="Email" rules={required('Email')}>
        <Input type="email" autoComplete="email" />
      </Form.Item>
      <Form.Item name="phone" label="Phone" rules={required('Phone')}>
        <Input />
      </Form.Item>
      <Form.Item name="comment" label="Comment" rules={required('Comment')}>
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item name="resolved" label="Resolved" valuePropName="checked">
        <Switch />
      </Form.Item>
    </>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <Space style={{ marginBottom: 8 }} align="center">
        <Typography.Text strong>Notifications</Typography.Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add notification
        </Button>
      </Space>
      <Table<DictionaryNotification>
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 720 }}
      />

      <Modal
        title="New notification"
        open={createOpen}
        afterOpenChange={(open) => {
          if (open) {
            createForm.setFieldsValue(CREATE_FORM_INITIAL);
          }
        }}
        onCancel={() => {
          setCreateOpen(false);
          createForm.setFieldsValue(CREATE_FORM_INITIAL);
        }}
        onOk={() => void submitCreate()}
        confirmLoading={busy}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false} initialValues={CREATE_FORM_INITIAL}>
          {formItems}
        </Form>
      </Modal>

      <Modal
        title="Edit notification"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
          editForm.resetFields();
        }}
        onOk={() => void submitEdit()}
        confirmLoading={busy}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          {formItems}
        </Form>
      </Modal>
    </div>
  );
}
